import { isEmpty } from "lodash";
import { ApiServices } from "./apiServices";

import {
  AuditActionTypeConstant,
  AuditDocumentTypes,
  Constants,
  DesignationConstants,
  FormatConstants,
  MasterDocumentTypeConstants,
  TypeConstants,
  permissionConstants,
} from "./keyConstants";
import constant from "services/constant";
import * as url from "services/url_helper";
import * as permissiomUrl from "pages/Permission/utils/urlHelper";
import { Box, Stack } from "@mui/material";
import { dispatch } from "store";
import { openSnackbar } from "store/reducers/snackbar";
import { v4 as uuidv4 } from "uuid";
import moment from "moment";
import CryptoJS from "crypto-js";
import dayjs from "dayjs";
import * as masterDocUrl from "pages/DynamicDocument/utils/urlHelper";
import slugify from "slugify";
import { GET_AUDIT_LOGS } from "pages/Audit-Trail/utils/urlHelper";
import { getUserDataByDesignation } from "./organogramServices";
import { getMasterDocDataByKey } from "./ReduxUtils";
import { isISODateString } from "components/CustomizeDynamicTable/services";

export const dynamicDateOperation = ({
  baseDate = moment(),
  months = 0,
  days = 0,
  hours = 0,
  minutes = 0,
  operation = "add",
} = {}) => {
  let currentDate = moment(baseDate);

  if (operation === "add") {
    currentDate = currentDate
      .add(months, "months")
      .add(days, "days")
      .add(hours, "hours")
      .add(minutes, "minutes");
  } else if (operation === "subtract") {
    currentDate = currentDate
      .subtract(months, "months")
      .subtract(days, "days")
      .subtract(hours, "hours")
      .subtract(minutes, "minutes");
  } else {
    throw new Error('Invalid operation. Use "add" or "subtract".');
  }

  return currentDate.format("YYYY-MM-DD HH:mm:ss");
};

// Local storage related functions
export const setItem = (key, value, encrypt = true) => {
  const secretKey = "wJ5@9F2!hYpZ6q#Xv$Lm%8dNvP3&bC";

  // For Value
  const valueString =
    typeof value === "object" ? JSON.stringify(value) : value?.toString();

  if (encrypt) {
    // Encrypt the value if the flag is true
    const encrypted = CryptoJS.AES.encrypt(valueString, secretKey).toString();
    localStorage.setItem(key, encrypted);
  } else {
    // Store value directly without encryption
    localStorage.setItem(key, valueString);
  }
};

const decryptItem = (encrypted, isEncrypted = true) => {
  const secretKey = "wJ5@9F2!hYpZ6q#Xv$Lm%8dNvP3&bC";

  if (!encrypted) {
    return null; // No data found for the given key
  }

  try {
    if (isEncrypted) {
      // Decrypt the encrypted string
      const bytes = CryptoJS.AES.decrypt(encrypted, secretKey);
      const decryptedString = bytes.toString(CryptoJS.enc.Utf8);

      // Try to parse as JSON (since the original value might be an object)
      try {
        return JSON.parse(decryptedString);
      } catch (error) {
        // If parsing fails, return as a plain string
        return decryptedString;
      }
    } else {
      // If the value is not encrypted, try to parse it directly as JSON
      try {
        return JSON.parse(encrypted);
      } catch (error) {
        // Return the value as a plain string if it's not JSON
        return encrypted;
      }
    }
  } catch (error) {
    console.error("Error decrypting data: ", error.message);
    return null; // Return null if decryption fails
  }
};

export const getItem = (key, isEncrypted = true) => {
  const encrypted = localStorage.getItem(key);
  const decryptedString = decryptItem(encrypted);
  return isEncrypted ? decryptedString : encrypted;
};

export const removeItem = (key) => {
  localStorage.removeItem(key);
};

// Mui Chip Color For Organogram Chart
export const getRandomColor = (designation) => {
  const colorMap = {
    "Managing Director": "info",
    Director: "primary",
  };

  const colors = ["default", "secondary", "error", "success", "warning"];

  return (
    colorMap[designation] || colors[Math.floor(Math.random() * colors.length)]
  );
};

export const getUser = () => {
  let user = localStorage.getItem(Constants.KEY_USER_DATA);
  const decryptedUser = decryptItem(user);
  if (!decryptedUser) {
    return null;
  } else {
    return decryptedUser;
  }
};

// function to check if value is not null and undefined
export const isNotNullOrUndefined = (value) =>
  value !== null && value !== undefined;

export const isNullOrEmpty = (value) =>
  value === null || value === undefined || value.length === 0;

// function to capital first letter of string
export const capitalizeFirstLetter = (string) =>
  string?.charAt(0)?.toUpperCase() + string?.slice(1)?.toLowerCase();

export const formatTimestampToUTC = (timestamp) => {
  const date = new Date(timestamp);
  return !isNaN(date) ? date?.toUTCString() : ""; // Return UTC string or empty string if invalid
};

export const logout = () => {
  localStorage.clear();
  window.location.replace("/");
};

export const verifyDomain = async () => {
  try {
    const { hostname } = window.location;
    setItem(Constants.KEY_HOST_NAME, hostname);

    const subDomain = hostname.split(".")[0];
    if (subDomain === "client") {
      return { validDomain: true };
    }

    const reqObj = {
      domainLink: hostname,
    };

    const uri = url.CHECK_VERIFIED_DOMAIN;
    const response = await ApiServices.callServicePostWithBodyData(uri, reqObj);

    const validDomain = response?.meta?.success || false;
    const data = response?.result || null;

    sessionStorage.setItem(`${subDomain}`, JSON.stringify(validDomain));

    return { validDomain, data };
  } catch (error) {
    console.error("CHECK_VERIFIED_DOMAIN error", error);
    return { validDomain: false, data: null };
  }
};

export const modifyQuestions = (questionsData, standardName) => {
  let data = {
    standard: standardName,
    sections: {},
  };

  questionsData?.forEach((question) => {
    const {
      section,
      questionContent,
      questionConstant,
      choices,
      parentQuestion,
      parentChoice,
      sectionConstant,
    } = question?.question;

    if (!data.sections[section]) {
      data.sections[section] = {
        constants: sectionConstant,
        docType: question?.docType,
        questions: {},
      };
    }

    if (question?.questionType === "mcq Question") {
      data.sections[section].questions[questionContent] = {
        constants: questionConstant,
        answer:
          question?.question?.answer?.answer === true
            ? true
            : question?.question?.answer?.answer === false
            ? false
            : question?.question?.answer === true
            ? true
            : question?.question?.answer === false
            ? false
            : null,
        daysOff: question?.question?.answer?.daysOff ?? null,
        type: "radio",
        parentQuestionId: question?._id,
        options: choices,
        suggestion: question?.question?.suggestions ?? "",
        questionFor: question?.docType,
        linkSection: question?.question?.linkSection ?? null,
        procedure: question?.question?.procedure ?? null,
        isLock: question?.isLock ?? false,
        isHide: question?.isHide ?? false,
      };
    } else if (question.questionType === "mcmc Question") {
      if (
        questionContent ===
        // "Provide Non-applicability of the quality management system. (if any requirement in clause 6, 7, or 8 of ISO 13485:2016 is not applicable due to the activities undertaken by the organization or the nature of the medical device, no need to include such requirement in the quality management system. for any clause that is determined to be non-applicable, provide the justification. Identified non applicable clause 6.4.2, 7.5.2, 7.5.3, 7.5.4, 7.5.5, 7.5.6, 7.5.7, 7.5.10, 8.3.4)"
        "Provide Non-applicability of the quality management system. (if any requirement in clause 6, 7, or 8 of ISO 13485:2016 is not applicable due to the activities undertaken by the organization or the nature of the medical device, no need to include such requirement in the quality management system."
      ) {
        data.sections[section].questions[questionContent] = {
          constants: questionConstant,
          answer: question.question.answer ? question?.question?.answer : [],
          type: "checkbox",
          parentQuestionId: question._id,
          options: choices,
          suggestion: question.question.suggestions ?? "",
          tableData: question.tableData != null ? question.tableData : [],
          questionFor: question.docType,
          linkSection: question?.question?.linkSection ?? null,
          procedure: question?.question?.procedure ?? null,
          isLock: question?.isLock ?? false,
          isHide: question?.isHide ?? false,
        };
      } else {
        data.sections[section].questions[questionContent] = {
          constants: questionConstant,
          answer: question.question.answer ? question?.question?.answer : [],
          type: "checkbox",
          parentQuestionId: question._id,
          options: choices,
          suggestion: question.question.suggestions ?? "",
          questionFor: question.docType,
          tableData:
            question.tableData === null || question.tableData === undefined
              ? getData(question.question.choices, question.question.answer)
              : question.tableData,
          linkSection: question?.question?.linkSection ?? null,
          procedure: question?.question?.procedure ?? null,
          isLock: question?.isLock ?? false,
          isHide: question?.isHide ?? false,
        };
      }
    } else if (question.questionType === "short Question") {
      if (parentQuestion && parentChoice) {
        // Ensure parentQuestion exists in questions object
        if (!data.sections[section].questions[parentQuestion]) {
          data.sections[section].questions[parentQuestion] = {};
        }

        // Ensure subquestion property exists for the parentQuestion
        if (!data.sections[section].questions[parentQuestion].subquestion) {
          data.sections[section].questions[parentQuestion].subquestion = {};
        }

        data.sections[section].questions[parentQuestion].subquestion[
          questionContent
        ] = {
          constants: questionConstant,
          answer: question.question.answer ? question?.question?.answer : "",
          type: "text",
          subQuestionId: question._id,
          suggestion: question.question.suggestions ?? "",
          questionFor: question.docType,
          linkSection: question?.question?.linkSection ?? null,
          procedure: question?.question?.procedure ?? null,
          isLock: question?.isLock ?? false,
          isHide: question?.isHide ?? false,
        };
      } else {
        data.sections[section].questions[questionContent] = {
          constants: questionConstant,
          answer: question.question.answer ? question?.question?.answer : "",
          type: "text",
          parentQuestionId: question._id,
          suggestion: question.question.suggestions ?? "",
          procedure:
            question?.question?.procedure !== undefined
              ? question?.question?.procedure
              : null,
          questionFor: question.docType,
          linkSection: question?.question?.linkSection ?? null,
          procedure: question?.question?.procedure ?? null,
          isLock: question?.isLock ?? false,
          isHide: question?.isHide ?? false,
        };
      }
    } else if (question.questionType === "Dropdown Question") {
      if (parentQuestion && parentChoice) {
        // Ensure parentQuestion exists in questions object
        if (!data.sections[section].questions[parentQuestion]) {
          data.sections[section].questions[parentQuestion] = {};
        }

        // Ensure subquestion property exists for the parentQuestion
        if (!data.sections[section].questions[parentQuestion].subquestion) {
          data.sections[section].questions[parentQuestion].subquestion = {};
        }
        data.sections[section].questions[parentQuestion].subquestion[
          questionContent
        ] = {
          constants: questionConstant,
          answer: question.question.answer ? question?.question?.answer : "",
          type: "dropdown",
          multiple: question?.question?.multiple,
          isType: question?.question?.isType ?? false,
          subQuestionId: question._id,
          // parentQuestionId: question._id,
          options: choices, // List of dropdown options
          suggestion: question.question.suggestions ?? "",
          linkSection: question?.question?.linkSection ?? null,
          procedure: question?.question?.procedure ?? null,
          questionFor: question.docType,
          isLock: question?.isLock ?? false,
          isHide: question?.isHide ?? false,
          isUnique: question?.question?.isUnique ?? undefined,
        };
      } else {
        data.sections[section].questions[questionContent] = {
          constants: questionConstant,
          answer: question.question.answer ? question?.question?.answer : "",
          type: "dropdown",
          multiple: question?.question?.multiple,
          parentQuestionId: question._id,
          isType: question?.question?.isType ?? false,
          options: choices, // List of dropdown options
          suggestion: question.question.suggestions ?? "",
          linkSection: question?.question?.linkSection ?? null,
          procedure: question?.question?.procedure ?? null,
          questionFor: question.docType,
          isLock: question?.isLock ?? false,
          isHide: question?.isHide ?? false,
          isUnique: question?.question?.isUnique ?? undefined,
        };
      }
    }
  });
  // Check if the answer contains either "Ethylene Oxide (EtO)" or "Gamma"
  const sterilizationAnswer =
    data?.sections?.["Product Realization "]?.questions[
      "Specify the method used for the sterilization of medical devices."
    ]?.answer;

  if (
    Array.isArray(sterilizationAnswer) &&
    (sterilizationAnswer.includes("ETO") ||
      sterilizationAnswer.includes("Gamma"))
  ) {
    // Access the related question and set isHide to true
    const sterilityQuestion =
      data?.sections?.["Product Realization "]?.questions[
        "Describe the validation method of another sterilization process"
      ];

    if (sterilityQuestion) {
      sterilityQuestion.isHide = true;
    }
  }

  const dataanalyzeVerify =
    data?.sections?.["Measurement, Analysis and Improvement"]?.questions[
      "Which type of data is analyzed to verify the effectiveness of QMS process?"
    ]?.answer;

  const justificationConst =
    data?.sections?.["Measurement, Analysis and Improvement"]?.questions[
      "Specify the Data Analysis Method."
    ];

  // Default to hide
  if (justificationConst) {
    justificationConst.isHide = true;
  }

  if (Array.isArray(dataanalyzeVerify)) {
    const includedAnswers = [
      "Feedback",
      "Complaints",
      "Supplier evaluation",
      "Audits",
      "Quality objectives",
      "Corrective Action and Preventive Action",
      "Service reports",
      "Conformity to product requirements different stages of production",
      "Analysis of product and process requirement",
    ];

    const hasRelevantAnswer = dataanalyzeVerify?.some((answer) =>
      includedAnswers?.includes(answer)
    );

    if (!hasRelevantAnswer && justificationConst) {
      justificationConst.isHide = false;
    }
  }
  return data;
};

const getData = (data, keys) => {
  const dataMap = data?.reduce((map, item) => {
    map[item?.choice] = item?.defaultData;
    return map;
  }, {});

  // Extract the defaultData arrays using the keys array
  const arrays = keys?.map((key) => dataMap[key] || []);

  // Create the output array by combining corresponding elements from the extracted arrays
  const result = arrays?.[0]?.map((_, index) => {
    const obj = {};
    keys?.forEach((key, keyIndex) => {
      const labelKey = key.replace(/\s+/g, "").toLowerCase(); // Convert 'Title' to 'title', 'Prepared By' to 'preparedby'
      // Check if defaultData array exists before accessing its elements
      obj[labelKey] = arrays[keyIndex][index]
        ? arrays?.[keyIndex]?.[index].label
        : null;
    });
    return obj;
  });

  return result;
};

export const dispatchFunction = (response) => {
  const message = response?.message || response?.result?.message;
  const messageType =
    response?.type || response?.result?.type || response?.response;
  if (isNotNullOrUndefined(message) && message !== "Fetched successfully") {
    dispatch(
      openSnackbar({
        open: true,
        message,
        variant: "alert",
        alert: {
          color:
            messageType === "success" || messageType === true
              ? "success"
              : "error",
        },
        close: false,
      })
    );
  }
};

export const formatMuiDate = (date, format = "DD-MM-YYYY") => {
  return dayjs(date).format(format);
};

export const showToast = (response, flag) => {
  const message = response?.meta?.message || response;
  const messageType = response?.meta?.success || flag || false;

  if (isNotNullOrUndefined(message) && message !== "Fetched successfully") {
    dispatch(
      openSnackbar({
        open: true,
        message,
        variant: "alert",
        alert: {
          color: messageType === true ? "success" : "error",
        },
        close: false,
      })
    );
  }
};

export const filterUserById = (userDataList) => {
  const userIds = userDataList
    ?.filter((user) => user?.userId || user?._id) // Filter out users with empty or null userId
    ?.map((user) => ({ userId: user?.userId || user?._id })); // Return objects with { userId: <userId value> }

  return userIds;
};

export const filterUserByIdAndDesignationId = (userDataList) => {
  return userDataList
    ?.filter((user) => user?.userId || user?._id)
    ?.map((user) => ({
      userId: user?.userId || user?._id,
      designationId: user?.designationId,
    }));
};

export const sendNotification = async (reqObj, isReturn = false) => {
  const URL = url.SEND_NOTIFICATIONS;
  let result = await ApiServices.callServicePostWithBodyData(URL, reqObj);
  if (isReturn) {
    return result;
  }
};

export const getDocumentLifeCycle = async (reqObj) => {
  const URL = url.GET_DOCUMENT_LIFE_CYCLE;
  return await ApiServices.callServicePostWithBodyData(URL, reqObj);
};

// for getting first user id and name by passing user array
export const extractUserIdAndName = (users) => {
  if (users?.length === 0) return null;
  const user = users?.[0];

  if (!user?.userId) {
    return null; // Return null if userId is an empty string
  }

  return {
    userId: user?.userId,
    name: user?.userName || user?.name,
    designation: user?.designation,
    designationId: user?.designationId,
  };
};

// for getting all user id and name by passing user array
export const extractUserDetails = (users) => {
  if (!Array.isArray(users) || users.length === 0) {
    return [];
  }

  return users
    ?.filter((user) => user?.userId) // Filter out users with empty or undefined userId
    ?.map((user) => ({
      userId: user?.userId,
      name: user?.userName || user?.name,
      designation: user?.designation,
      designationId: user?.designationId,
    }));
};

// for getting first user id and name by passing object
export const extractUserIdAndNameByObject = (user) => {
  if (!user) return null;

  if (!user?.userId) {
    return null; // Return null if userId is an empty string
  }

  return {
    userId: user?.userId,
    name: user?.userName || user?.name,
    designation: user?.designation,
    designationId: user?.designationId,
  };
};

export const getUserData = (userData) => {
  if (!userData) return null;
  let selectedRole = getItem(Constants.KEY_SELECTED_ROLE);

  return {
    userId: userData?._id || null,
    isTopManagement:
      selectedRole?.departmentDefaultName === "Top Management" ? true : false,
    name: userData?.name || null,
    designation:
      selectedRole?.roleName || userData?.externalUser?.type || userData?.role,
    designationId: selectedRole?.roleId || null,
    userDesignation:
      `${userData?.name}-${selectedRole?.roleName}` ||
      userData?.name ||
      null + selectedRole?.roleName ||
      userData?.externalUser?.type ||
      userData?.role,
  };
};

// get the string length by passing number
export const hasValidLength = (value, length = 0, comparison) => {
  const stringLength = String(value)?.length;
  if (comparison === ">") {
    return stringLength > length;
  } else if (comparison === "===") {
    return stringLength === length;
  }
  // If no comparison is provided, just check if length is greater than the default length
  return stringLength > length;
};

// Convert the entire string to lowercase
export const toLowercaseString = (str) => {
  return str ? str.toLowerCase() : null;
};

export const toCamelCase = (str) => {
  return str
    .replace(/(?:^\w|[A-Z]|\b\w|\s+|_|\-|\s)/g, (match, index) =>
      index === 0 ? match.toLowerCase() : match.toUpperCase()
    )
    .replace(/\s+/g, "");
};

export function camelCaseToString(camelCaseStr) {
  return (
    camelCaseStr
      // Replace uppercase letters with space + lowercase equivalent
      .replace(/([A-Z])/g, " $1")
      // Capitalize the first letter of the result
      .replace(/^./, function (str) {
        return str.toUpperCase();
      })
  );
}

// function for converting into camcelCaseToNormal
export const camelCaseToNormal = (str) =>
  str.replace(/([a-z])([A-Z])/g, "$1 $2").replace(/^./, (c) => c.toUpperCase());

export const generateUUID = () => uuidv4();

export function formatKey(key) {
  return (
    key
      .replace(/([a-z])([A-Z])/g, "$1 $2")
      .charAt(0)
      .toUpperCase() + key.slice(1).replace(/[A-Z]/g, " $&")
  );
}

export const getTitle = (title) => {
  return (
    <Stack
      direction={"row"}
      spacing={1}
      justifyContent="space-between"
      alignItems="center"
      sx={{ p: 1, pb: 0, fontSize: 20 }}
    >
      <h5>{title}</h5>
    </Stack>
  );
};

export const getConFigData = (questions) => {
  const result = {};
  const traverse = (obj) => {
    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        if (typeof obj[key] === "object") {
          traverse(obj[key]);
        } else {
          if (key === "constants" && obj) {
            result[obj.constants] = obj?.answer ?? "";
            if (obj?.tableData && obj?.tableData?.length > 0) {
              result[obj.constants] = obj?.tableData ?? [];
            }
          }
        }
      }
    }
  };
  traverse(questions);
  return result;
};
export const formatDate = (dateString) => {
  const date = new Date(dateString);
  const options = {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "numeric",
    minute: "numeric",
  };
  return date != "Invalid Date"
    ? date.toLocaleDateString(undefined, options)
    : "N/A";
};

export const formatDateWithoutTime = (dateString) => {
  const date = new Date(dateString);
  const options = {
    // weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  };
  return date != "Invalid Date"
    ? date.toLocaleDateString(undefined, options)
    : "N/A";
};
export const toPascalCase = (str) => {
  return str
    .replace(
      /\w\S*/g,
      (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
    )
    .replace(/\s+/g, "");
};

// for generating date and time based on date
export const renderTimestamp = (
  item,
  singleLine = false,
  type = "dateTime"
) => {
  const date = item ? new Date(item) : null;
  const formattedDate = date ? date.toLocaleDateString() : "-";
  const formattedTime = date ? date.toLocaleTimeString() : "-";

  const renderContent = () => {
    switch (type) {
      case "date":
        return formattedDate;
      case "time":
        return formattedTime;
      default: // "dateTime"
        return singleLine ? (
          `${formattedDate} ${formattedTime}`
        ) : (
          <>
            <div className="timestamp-date">{formattedDate}</div>
            <div className="timestamp-time">{formattedTime}</div>
          </>
        );
    }
  };

  return renderContent();
};

export const validateEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

// for downloading file
export const downloadFile = async ({ path, docName, docNumber }) => {
  const proxyUrl = constant.PROXY_URL;

  try {
    const response = await fetch(
      `${proxyUrl}?pdfLocation=${encodeURIComponent(path)}`,
      { method: "GET" }
    );

    if (!response.ok) throw new Error("Network response was not ok");

    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");

    let finalDocNumber = docNumber ? `${docName}-${docNumber}` : `${docName}`;

    // Replace all '.' with '/'
    finalDocNumber = finalDocNumber.replaceAll(".", "/");
    link.href = url;
    link.download = finalDocNumber;
    document.body.appendChild(link);

    link.click();

    // Cleanup
    link.remove();
    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.error("Error downloading file:", error);
  }
};

export const checkAuthorization = (data) => {
  let details = data?.map((item) => item?.userId).flat();
  let userData = getItem(Constants.KEY_USER_DATA);
  return details?.includes(userData._id);
};

export const getAllUserIds = (data, excludeIds = []) => {
  return data
    ?.filter(
      (item) =>
        item?.userId &&
        item?.designationId &&
        Object.keys(item?.userName || {}).length > 0 &&
        !excludeIds.includes(item?.userId)
    )
    ?.map((item) => ({
      userId: item.userId,
      designationId: item.designationId,
    }));
};

export const getYearFromDate = (dateString) => {
  return moment(dateString).year();
};

export const validationDynamicTable = (rows, columns) => {
  const newErrors = {};
  rows?.forEach((row, rowIndex) => {
    columns?.forEach((col) => {
      const { value, displayValue, validation } = col;
      if (validation) {
        if (validation.required && !row[displayValue ?? value]) {
          if (!newErrors[rowIndex]) newErrors[rowIndex] = {};
          newErrors[rowIndex][displayValue ?? value] = validation?.msg;
        }
      }
    });
  });
  return newErrors;
};

export const validateColumns = (columns) => {
  const newErrors = {};

  // Iterate over the columns and validate each one
  columns?.forEach((col) => {
    const { value, validation, inputValue } = col;

    // Determine if the column should be hidden
    const isHidden =
      col?.hidden?.key && col?.hidden?.value && col?.hidden?.checkingField
        ? columns?.find(
            (item) => item?.[col?.hidden?.checkingField] === col?.hidden?.key
          )?.inputValue !== col.hidden.value
        : false;

    // Skip validation for hidden columns
    if (isHidden) {
      return;
    }

    // Validate required fields
    if (validation?.required && !inputValue) {
      newErrors[value] = validation?.msg || "This field is required.";
    }
    // Validate pattern if defined
    if (validation?.pattern && inputValue) {
      const patternInString = convertPattern(validation.pattern);
      const regex = new RegExp(patternInString);
      if (!regex.test(inputValue)) {
        newErrors[value] = validation?.patternMsg || "Invalid format.";
      }
    }
  });

  return newErrors;
};

const convertPattern = (patternString) => {
  // Check if the pattern includes delimiters (e.g., /pattern/flags)
  const regexParts = patternString?.match(/^\/(.*?)\/([gimsuy]*)$/);

  if (regexParts) {
    // If delimiters exist, return the pattern as a RegExp object
    return new RegExp(regexParts[1], regexParts[2]);
  } else {
    // If no delimiters, treat the entire string as the pattern
    return new RegExp(patternString);
  }
};

export const downloadFormat = async (url, reqObj, onChangeDownloadState) => {
  let finalObj = reqObj;
  // Show the toast indicating download has started
  showToast("File started to download", true);

  if (finalObj?.auditData) {
    let userData = getItem(Constants.KEY_USER_DATA);
    let defaultMasterDocData = getMasterDocDataByKey({
      masterDocId: finalObj?.auditData?.masterDocId,
    });
    finalObj.auditObj = generateAuditObj({
      documentId: reqObj?.documentId || reqObj?.formatId,
      changedFields: [],
      documentType: AuditDocumentTypes.Record,
      documentName: defaultMasterDocData?.name,
      action: AuditActionTypeConstant.Download,
      actionMessage: `${userData?.name}-${selectedRole?.roleName} Download ${defaultMasterDocData?.name}`,
      masterDocId: defaultMasterDocData?.masterDocId,
      locationPath: finalObj?.navigateUrl,
    });

    delete finalObj?.auditData;
  }

  try {
    // Set the download state to true
    onChangeDownloadState(true);

    // Make an API call to fetch the file
    const result = await ApiServices.callServicePostWithBodyData(url, reqObj);
    // Show the result via toast
    showToast(result);

    if (result?.meta?.success) {
      const pdfData = result?.data;

      // Download the file from the PDF location
      await downloadFile(pdfData);
    }
  } catch (error) {
    console.error("Download error:", error);
    showToast("Error during download", false);
  } finally {
    // Reset the download state to false
    onChangeDownloadState(false);
  }
};

// for getting nested value of object
export const getNestedValue = (obj, field) => {
  // If the field includes a dot, treat it as a nested field
  if (field?.includes(".")) {
    const keys = field?.split("."); // Split the field by dot notation
    return keys?.reduce((acc, key) => (acc ? acc[key] : undefined), obj); // Iterate through keys to access the nested value
  } else {
    // If no dot, simply access the field directly
    return obj[field];
  }
};

// for getting master doc data by defaultName
export const getDocumentLifeCycleForMasterDoc = async (
  documentName,
  moduleType,
  selectFields = "defaultName,name,moduleType,signatureUsers"
) => {
  try {
    let reqObj = {
      defaultName: documentName,
      selectFields: selectFields,
      ...(moduleType && { moduleType: moduleType }),
    };

    const queryString = new URLSearchParams(reqObj).toString();
    const uri = `${masterDocUrl.GET_DATA_BY_DEFAULTNAME}?${queryString}`;
    let result = await ApiServices.callServiceGet(uri);
    if (result?.meta?.success) {
      return result?.data;
    } else {
      showToast(result);
    }
  } catch (error) {
    console.error("CREATE_MASTER_TABLE ERROR", error);
  }
};

// for getting master doc data by defaultName With Format
export const getDocumentLifeCycleForMasterDocWithFormat = async (
  documentName,
  moduleType,
  selectFields = "defaultName,name,moduleType,signatureUsers"
) => {
  try {
    let reqObj = {
      defaultName: documentName,
      selectFields: selectFields,
      ...(moduleType && { moduleType: moduleType }),
      isFormat: true,
    };

    const queryString = new URLSearchParams(reqObj).toString();
    const uri = `${masterDocUrl.GET_DATA_BY_DEFAULTNAME}?${queryString}`;
    let result = await ApiServices.callServiceGet(uri);
    if (result?.meta?.success) {
      return result?.data;
    } else {
      showToast(result);
    }
  } catch (error) {
    console.error(error);
  }
};

// for getting format data by documentName
export const getFormatDataById = async (
  documentName,
  moduleType = MasterDocumentTypeConstants.FORMAT,
  constant = null,
  selectFields = "_id,structuredData,structureType,masterDocId"
) => {
  try {
    const queryParams = {
      defaultName: documentName,
      selectFields: selectFields,
      moduleType: moduleType,
      ...(isNotNullOrUndefined(constant) && { constant }),
    };

    let result = await ApiServices.callServiceGet(
      buildUrl(url?.GET_FORMAT, queryParams)
    );

    if (result?.meta?.success) {
      return result?.data;
    } else if (result) {
      if (result?.meta?.isShow) {
        showToast(result);
      }
      if (
        [
          FormatConstants.PURCHASE.RAW_MATERIAL_COMPONENT,
          FormatConstants.PURCHASE.ACCESSORIES,
          FormatConstants.PURCHASE.PACKAGING_MATERIAL,
        ]?.includes(documentName)
      ) {
        showToast(result?.message);
      }
      return result?.data;
    } else {
      showToast(result);
    }
  } catch (error) {
    console.error(error);
  }
};

// for getting masterDocData By Id
export const getMasterDocDataById = async (id, isVersion = true) => {
  try {
    const queryParams = {
      masterDocId: id,
      isVersion: isVersion,
    };
    let result = await ApiServices.callServiceGet(
      buildUrl(masterDocUrl?.GET_NAME_BY_ID, queryParams)
    );
    if (result?.meta?.success) {
      const versionOptions = result?.data?.versionData
        ?.filter((item) => item?.revisionNumber) // Only include items with revisionNumber
        ?.map((item) => ({
          label: item?.revisionNumber,
          value: item?._id,
          isActive: item?.isActive,
        }));

      // Append versionOptions into result.data
      return {
        ...result?.data,
        versionOptions: versionOptions ?? [],
      };
    } else {
      showToast(result);
    }
  } catch (error) {
    console.error(error);
  }
};

let userData = getItem(Constants.KEY_USER_DATA);

// for getting format data by documentName
export const getPermssionDataByName = async (
  documentName = undefined,
  documentType = MasterDocumentTypeConstants.RECORD,
  extraData = undefined
) => {
  try {
    // If `userData?.externalUser` exists, return an empty array
    if (userData?.externalUser) {
      return [];
    }
    const reqObj = {
      ...(documentName !== undefined
        ? {
            defaultName: Array.isArray(documentName)
              ? documentName
              : [documentName],
          }
        : {}),
      documentType: documentType,
      ...(extraData !== undefined ? extraData : {}),
    };
    let result = await ApiServices.callServicePostWithBodyData(
      permissiomUrl?.GET_PERMISSSION_BY_NAME,
      reqObj
    );

    if (result?.meta?.success) {
      return result?.data;
    } else {
      return [];
    }
  } catch (error) {
    console.error(error);
  }
};

const selectedRole = getItem(Constants.KEY_SELECTED_ROLE) || {};
export const checkPermission = (
  data,
  defaultName,
  permissionKey = permissionConstants.CREATE
) => {
  if (selectedRole?.departmentDefaultName === "Top Management") {
    return true;
  } else {
    if (Array.isArray(data)) {
      const doc = data?.find(
        (item) => (item?.defaultName ?? item?.slug) === defaultName
      );
      return doc ? doc?.permission[permissionKey] || false : false;
    } else {
      return false;
    }
  }
};

export const getExternalFormatDataById = async (
  documentName,
  orgId,
  selectFields = "_id,structuredData,structureType,masterDocId"
) => {
  try {
    const queryParams = {
      defaultName: documentName,
      selectFields: selectFields,
      orgId: orgId,
    };
    let result = await ApiServices.callServiceGet(
      buildUrl(url?.GET_EXTERNAL_FORMAT, queryParams)
    );
    if (result?.meta?.success) {
      return result?.data;
    } else {
      showToast(result);
    }
  } catch (error) {
    console.error(error);
  }
};

// make build url for search query
export const buildUrl = (base, params) => {
  const queryString = new URLSearchParams(params).toString();
  return `${base}?${queryString}`;
};

// for convert to kebeb case
export const ConvertToKebabCase = (text) => {
  // Ensure text is a string and preprocess to handle camelCase/PascalCase
  const formattedText = text
    ? text.replace(/([a-z])([A-Z])/g, "$1-$2") // Insert hyphen before capital letters
    : "";

  return slugify(formattedText, {
    lower: true, // Convert to lowercase
    strict: true, // Remove special characters
  });
};

// for convert from kebeb case to normal
export const kebabToNormal = (kebabString) =>
  kebabString
    ?.split("-") // Split the string by hyphens
    .map((word) => word?.charAt(0)?.toUpperCase() + word?.slice(1)) // Capitalize each word
    .join(" ");

// for formatting time
export const formattedTime = (time) =>
  time ? moment(time).format("YYYY-MM-DD / HH:mm:ss") : "";

// for formatting date
export const formatDateValue = (dateString) => {
  return moment(dateString).format("YYYY-MM-DD");
};

// for getting ISO Date
export const getISODate = (date = new Date()) => {
  return new Date(date).toISOString();
};

export const formattedTimeMins = (dateString) => {
  return dateString ? moment(dateString).format("YYYY-MM-DD / HH:mm") : "";
};

export const getMonthRange = () => {
  return [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];
};

export const getYearRange = () => {
  const currentYear = new Date().getFullYear();
  const years = [];

  for (let i = 0; i <= 5; i++) {
    years?.push(currentYear - i);
  }

  return years;
};

export const fetchRecordDocs = async ({ selectedRole, userData, id }) => {
  const reqObj = {
    moduleType: "record",
    designationId: selectedRole?.roleId,
    clientUserId: userData?._id || null,
    parentId: id,
  };

  const uri = buildUrl(permissiomUrl.GET_DOCS_FOR_RECORD, reqObj);
  const result = await ApiServices.callServiceGet(uri);

  return result;
};

export const getDocTitle = (id, masterDocName) => {
  const name = masterDocName;
  return `${id ? "Edit " : ""}${name}`;
};

export const getValueFromQuestionWithConst = async (constants) => {
  try {
    const queryParams = {
      questionConstant: constants,
    };
    let result = await ApiServices.callServiceGet(
      buildUrl(url.GET_QUESTION_VALUE_FROM_CONSTANT, queryParams)
    );
    return result?.data;
  } catch (error) {
    console.error("GET_QUESTION_VALUE_FROM_CONSTANT", error);
  }
};

export const hasDataChanged = (originalRow, updatedRow) => {
  return JSON.stringify(originalRow) !== JSON.stringify(updatedRow);
};

export const generateAuditObj = ({
  documentType,
  documentName,
  action,
  actionMessage,
  documentId,
  masterDocId,
  changedFields,
  locationPath,
  subDetails,
  orgId,
  isTopManagement,
  childAuditDetails,
}) => {
  const baseAuditObj = {
    userId: userData?._id,
    departmentId: selectedRole?.departmentUuid,
    designationId: selectedRole?.roleId,
    documentType: documentType ?? AuditDocumentTypes.Record,
    documentName: documentName ?? "",
    action: action ?? AuditActionTypeConstant.View,
    actionMessage: actionMessage ?? "",
    subDetails: subDetails ?? "",
    documentId: documentId,
    masterDocId: masterDocId,
    changedFields: changedFields ?? [],
    locationPath:
      locationPath ?? `${window.location.pathname.replace(/\/$/, "")}`,
    ...(orgId !== undefined && { orgId }),
    ...(isTopManagement !== undefined && { isTopManagement }),
  };

  if (childAuditDetails) {
    const detailsArray = Array.isArray(childAuditDetails)
      ? childAuditDetails
      : [childAuditDetails];

    baseAuditObj.childAuditDetails = detailsArray.map((detail) => ({
      ...detail,
      // documentType: documentType ?? AuditDocumentTypes.Record,
    }));
  }

  return baseAuditObj;
};

export const createReadLog = async ({
  masterDocData,
  documentType,
  locationPath,
  documentId,
  changedFields,
}) => {
  const reqObj = generateAuditObj({
    documentId: documentId,
    changedFields: changedFields ?? [],
    documentType: documentType ?? AuditDocumentTypes.Record,
    documentName: masterDocData?.name,
    action: AuditActionTypeConstant.View,
    actionMessage: `${userData?.name}-${selectedRole?.roleName} has Viewed ${masterDocData?.name}.`,
    masterDocId: masterDocData?._id || masterDocData?.masterDocId,
    locationPath: locationPath,
  });

  const uri = GET_AUDIT_LOGS;
  const result = await ApiServices.callServicePostWithBodyData(uri, reqObj);

  return result;
};

export const getAllAuditChanges = (auditData = []) => {
  const allChanges = [];

  const getRowId = (row) => row?._id || row?.id || row?.uuid || null;

  auditData?.forEach((dataItem) => {
    const {
      tableName,
      tableType,
      newColumns = [],
      oldColumns = [],
      columns = [],
      oldRows = [],
      newRows = [],
    } = dataItem;

    if (tableType === "Form-Table") {
      const changes = [];

      newColumns?.forEach((newCol) => {
        const oldCol = oldColumns?.find((c) => c?.value === newCol?.value);
        if (!oldCol) return;

        const getDisplayValue = (col) => {
          const val = col?.inputValue ?? "";

          if (Array.isArray(val)) {
            return JSON.stringify(
              col?.renderOptionValue
                ? val?.map((item) => item?.[col?.renderOptionValue])
                : val
            );
          }

          if (typeof val === "object" && val !== null) {
            return JSON.stringify(
              col?.renderOptionValue ? val?.[col?.renderOptionValue] : val
            );
          }

          return JSON.stringify(val);
        };

        const oldVal = getDisplayValue(oldCol);
        const newVal = getDisplayValue(newCol);

        if (oldVal !== newVal) {
          changes.push({
            ...newCol,
            tableName,
            fieldName: newCol?.name || newCol?.value,
            fieldType: newCol?.type || "text",
            oldValue: oldCol?.inputValue ?? "",
            newValue: newCol?.inputValue ?? "",
            renderOptionValue: newCol?.renderOptionValue,
          });
        }
      });

      if (changes.length > 0) {
        allChanges.push({
          tableName,
          tableType,
          changes,
        });
      }
    }
    if (tableType === "Dynamic-Table") {
      const changedColumnsSet = new Set();
      const changedRows = [];

      const oldHasId = oldRows?.some((r) => getRowId(r));
      const newHasId = newRows?.some((r) => getRowId(r));
      const useIdComparison = oldHasId && newHasId;

      // Track modified rows
      oldRows?.forEach((oldRow, index) => {
        const match = newRows?.find((newRow, idx) => {
          if (useIdComparison) {
            const oldId = getRowId(oldRow);
            const newId = getRowId(newRow);
            return oldId && newId && oldId === newId;
          }
          return index === idx;
        });

        if (match) {
          const changedCols = [];

          columns?.forEach((col) => {
            const key = col?.value;
            if (!key) return;

            const val1 = col?.renderOptionValue
              ? Array.isArray(oldRow?.[key])
                ? oldRow[key]
                    .map((item) => item?.[col.renderOptionValue])
                    .join(", ")
                : oldRow?.[key]?.[col.renderOptionValue]
              : col?.renderValue
              ? Array.isArray(oldRow?.[key])
                ? oldRow[key].map((item) => item?.[col.renderValue]).join(", ")
                : oldRow?.[key]?.[col.renderValue]
              : oldRow?.[key];

            const val2 = col?.renderOptionValue
              ? Array.isArray(match?.[key])
                ? match[key]
                    .map((item) => item?.[col.renderOptionValue])
                    .join(", ")
                : match?.[key]?.[col.renderOptionValue]
              : col?.renderValue
              ? Array.isArray(match?.[key])
                ? match[key].map((item) => item?.[col.renderValue]).join(", ")
                : match?.[key]?.[col.renderValue]
              : match?.[key];

            const isDifferent = JSON.stringify(val1) !== JSON.stringify(val2);

            if (isDifferent) {
              changedCols.push(col);
              changedColumnsSet.add(key);
            }
          });

          if (changedCols.length > 0) {
            changedRows.push({
              status: "modified",
              oldRow,
              newRow: match,
            });
          }
        }
      });

      // Track added rows
      newRows?.forEach((newRow, index) => {
        const exists = oldRows?.some((oldRow, idx) => {
          if (useIdComparison) {
            const newId = getRowId(newRow);
            const oldId = getRowId(oldRow);
            return newId && oldId && newId === oldId;
          }
          return index === idx;
        });

        if (!exists) {
          columns?.forEach((col) => changedColumnsSet.add(col.value));
          changedRows.push({
            status: "added",
            oldRow: null,
            newRow,
          });
        }
      });

      // Track deleted rows
      oldRows?.forEach((oldRow, index) => {
        const exists = newRows?.some((newRow, idx) => {
          if (useIdComparison) {
            const newId = getRowId(newRow);
            const oldId = getRowId(oldRow);
            return newId && oldId && newId === oldId;
          }
          return index === idx;
        });

        if (!exists) {
          columns?.forEach((col) => changedColumnsSet.add(col.value));
          changedRows.push({
            status: "deleted",
            oldRow,
            newRow: null,
          });
        }
      });

      if (changedColumnsSet.size > 0) {
        const filteredColumns = columns.filter((col) =>
          changedColumnsSet.has(col.value)
        );

        allChanges.push({
          tableName,
          tableType,
          changes: {
            columns: filteredColumns,
            rows: changedRows,
          },
        });
      }
    }
    if (tableType === "Children-Table") {
      const changedColumnsSet = new Set();
      const changedRows = [];

      // Helper function to flatten row with children
      const flattenRow = (row, rowIndex) => {
        const flatEntries = [];

        // Add parent row data
        const parentData = { ...row };
        delete parentData.children; // Remove children to avoid duplication

        if (row?.children && Array.isArray(row?.children)) {
          row?.children?.forEach((child, childIndex) => {
            flatEntries.push({
              ...parentData,
              ...child,
              _parentIndex: rowIndex,
              _childIndex: childIndex,
              _flatId: `${rowIndex}_${childIndex}`,
            });
          });
        } else {
          // If no children, still add the parent row
          flatEntries.push({
            ...parentData,
            _parentIndex: rowIndex,
            _childIndex: -1,
            _flatId: `${rowIndex}_-1`,
          });
        }

        return flatEntries;
      };

      // Flatten old and new rows
      const flatOldRows =
        oldRows?.flatMap((row, index) => flattenRow(row, index)) || [];
      const flatNewRows =
        newRows?.flatMap((row, index) => flattenRow(row, index)) || [];

      // Get all column keys from both parent and children columns
      const allColumnKeys = new Set();
      columns?.forEach((col) => {
        if (col.value) {
          allColumnKeys.add(col.value);
        }
        if (Array.isArray(col.value)) {
          col.value.forEach((childCol) => {
            if (childCol.value) {
              allColumnKeys.add(childCol.value);
            }
          });
        }
      });

      // Track modified rows
      flatOldRows?.forEach((oldFlatRow) => {
        const matchingNewRow = flatNewRows?.find(
          (newFlatRow) => newFlatRow._flatId === oldFlatRow._flatId
        );

        if (matchingNewRow) {
          const changedCols = [];

          allColumnKeys.forEach((key) => {
            const oldVal = oldFlatRow[key];
            const newVal = matchingNewRow[key];

            if (JSON.stringify(oldVal) !== JSON.stringify(newVal)) {
              changedCols.push({ value: key, name: key });
              changedColumnsSet.add(key);
            }
          });

          if (changedCols.length > 0) {
            changedRows.push({
              status: "modified",
              oldRow: oldFlatRow,
              newRow: matchingNewRow,
              parentIndex: oldFlatRow._parentIndex,
              childIndex: oldFlatRow._childIndex,
            });
          }
        }
      });

      // Track added rows
      flatNewRows?.forEach((newFlatRow) => {
        const exists = flatOldRows?.some(
          (oldFlatRow) => oldFlatRow._flatId === newFlatRow._flatId
        );

        if (!exists) {
          allColumnKeys.forEach((key) => changedColumnsSet.add(key));
          changedRows.push({
            status: "added",
            oldRow: null,
            newRow: newFlatRow,
            parentIndex: newFlatRow._parentIndex,
            childIndex: newFlatRow._childIndex,
          });
        }
      });

      // Track deleted rows
      flatOldRows?.forEach((oldFlatRow) => {
        const exists = flatNewRows?.some(
          (newFlatRow) => newFlatRow._flatId === oldFlatRow._flatId
        );

        if (!exists) {
          allColumnKeys.forEach((key) => changedColumnsSet.add(key));
          changedRows.push({
            status: "deleted",
            oldRow: oldFlatRow,
            newRow: null,
            parentIndex: oldFlatRow._parentIndex,
            childIndex: oldFlatRow._childIndex,
          });
        }
      });

      if (changedColumnsSet.size > 0) {
        const filteredColumns = Array.from(changedColumnsSet).map((key) => ({
          value: key,
          name: key,
        }));

        allChanges.push({
          tableName,
          tableType,
          changes: {
            columns: filteredColumns,
            rows: changedRows,
          },
        });
      }
    }
    if (dataItem.tableType === "Raw-Material-Table") {
      const result = detectChangesRawMaterialChanges(dataItem);
      if (result) {
        allChanges.push(result);
      }
    }
  });

  return allChanges;
};

const extractColumnObjects = (columns = []) => {
  const result = {};

  const traverse = (cols, level) => {
    cols.forEach((col) => {
      if (Array.isArray(col.value)) {
        if (level === "variant") {
          col.value.forEach((variantCol) => {
            if (!Array.isArray(variantCol.value)) {
              result[variantCol.value] = variantCol;
            } else if (level === "dimension" || level === "inspection") {
              variantCol.value.forEach((dimensionCol) => {
                if (dimensionCol?.defaultValue === "dimensionWithUnit") {
                  result["measurementNumber"] = {
                    value: "measurementNumber",
                    ...dimensionCol,
                  };
                  result["siUnit"] = { value: "siUnit", ...dimensionCol };
                  result[dimensionCol.value] = dimensionCol;
                } else {
                  result[dimensionCol.value] = dimensionCol;
                }
              });
            }
          });
        }
      } else {
        result[col.value] = col;
      }
    });
  };

  traverse(columns, "parent");
  traverse(columns, "variant");
  traverse(columns, "dimension");
  traverse(columns, "inspection");

  return result;
};

const detectChangesRawMaterialChanges = (dataItem) => {
  const {
    tableName,
    tableType,
    columns = [],
    oldRows = [],
    newRows = [],
  } = dataItem;

  const changedColumnsSet = new Set();
  const changedRows = [];

  // Helper function to get row identifier
  const getRowId = (row) => row?._id || row?.id || row?.uuid || null;

  // Utility function to get flat keys from columns based on the level
  const getColumnKeys = (cols, level) => {
    let keys = [];
    cols.forEach((col) => {
      if (Array.isArray(col.value)) {
        if (level === "variant") {
          col?.value?.forEach((variantCol) => {
            if (!Array.isArray(variantCol?.value)) {
              keys.push(variantCol.value);
            }
          });
        } else if (level === "dimension" || level === "inspection") {
          col?.value?.forEach((variantCol) => {
            if (Array.isArray(variantCol.value)) {
              variantCol?.value?.forEach((dimensionCol) => {
                if (dimensionCol?.defaultValue === "dimensionWithUnit") {
                  keys.push(dimensionCol?.value);
                  keys.push("measurementNumber");
                  keys.push("siUnit");
                } else {
                  keys.push(dimensionCol?.value);
                }
              });
            }
          });
        }
      } else if (level === "parent") {
        keys.push(col?.value);
      }
    });
    return keys;
  };

  const parentKeys = getColumnKeys(columns, "parent");
  const variantKeys = getColumnKeys(columns, "variant");
  const dimensionKeys = getColumnKeys(columns, "dimension");
  const inspectionKeys = getColumnKeys(columns, "inspection");

  // Helper function to create flattened rows following the same pattern as your reference
  const createFlattenedRows = (row, rowIndex) => {
    if (!row || typeof row !== "object") return [];
    let flatRows = [];

    if (!row?.variant || row?.variant?.length === 0) {
      // Parent level
      let flatRow = {
        path: `${rowIndex}`,
        index: rowIndex + 1,
        level: "parent",
        parentIndex: rowIndex,
        id: row?.id,
      };

      parentKeys?.forEach((key) => {
        flatRow[key] = row[key];
      });

      flatRows.push(flatRow);
      return flatRows;
    }

    row?.variant?.forEach((variant, variantIndex) => {
      if (!variant.dimensionData || variant.dimensionData.length === 0) {
        // Variant level
        let flatRow = {
          path: `${rowIndex}.variant[${variantIndex}]`,
          index: rowIndex + 1,
          level: "variant",
          parentIndex: rowIndex,
          variantIndex: variantIndex,
          variantId: variant?.variantId,
        };

        // Add parent keys for context
        parentKeys.forEach((key) => {
          flatRow[key] = row[key];
        });

        variantKeys.forEach((key) => {
          flatRow[key] = variant[key];
        });

        flatRows.push(flatRow);
        return;
      }

      variant?.dimensionData?.forEach((dimension, dimensionIndex) => {
        if (
          !dimension.inspectionData ||
          dimension.inspectionData.length === 0
        ) {
          // Dimension level
          let flatRow = {
            path: `${rowIndex}.variant[${variantIndex}].dimensionData[${dimensionIndex}]`,
            index: rowIndex + 1,
            level: "dimension",
            parentIndex: rowIndex,
            variantIndex: variantIndex,
            dimensionIndex: dimensionIndex,
            variantId: variant?.variantId,
            dimensionId: dimension?.dimensionId,
          };

          parentKeys?.forEach((key) => {
            flatRow[key] = row[key];
          });

          variantKeys?.forEach((key) => {
            flatRow[key] = variant[key];
          });

          // Properly handle dimension data
          dimensionKeys?.forEach((key) => {
            if (key === "measurementNumber") {
              flatRow[key] = dimension?.measurementNumber;
            } else if (key === "siUnit") {
              flatRow[key] = dimension?.siUnit;
            } else {
              flatRow[key] = dimension[key];
            }
          });

          flatRows.push(flatRow);
          return;
        }

        dimension?.inspectionData?.forEach((inspection, inspectionIndex) => {
          // let flatRow = {
          //   path: `${rowIndex}.variant[${variantIndex}].dimensionData[${dimensionIndex}].inspectionData[${inspectionIndex}]`,
          //   index: rowIndex + 1,
          //   level: "inspection",
          //   parentIndex: rowIndex,
          //   variantIndex: variantIndex,
          //   dimensionIndex: dimensionIndex,
          //   inspectionIndex: inspectionIndex,
          //   variantId: variant?.variantId,
          //   dimensionId: dimension?.dimensionId,
          //   id: row?.id,
          // };
          let flatRow = {
            path: `${rowIndex}.variant[${variantIndex}].dimensionData[${dimensionIndex}].inspectionData[${inspectionIndex}]`,
            index: rowIndex + 1,
            level: "inspection",
            parentIndex: rowIndex,
            variantIndex: variantIndex,
            dimensionIndex: dimensionIndex,
            inspectionIndex: inspectionIndex,
            variantId: variant?.variantId,
            dimensionId: dimension?.dimensionId,
            inspectionId: inspection?.inspectionId, // <-- new
            id: row?.id,
          };

          parentKeys.forEach((key) => {
            flatRow[key] = row[key];
          });

          variantKeys.forEach((key) => {
            flatRow[key] = variant[key];
          });

          // Dimension data
          flatRow.dimension = dimension.dimension;
          flatRow.measurementNumber = dimension.measurementNumber;
          flatRow.siUnit = dimension.siUnit;

          // Inspection data
          Object.keys(inspection).forEach((key) => {
            flatRow[key] = inspection[key];
          });

          flatRows.push(flatRow);
        });
      });
    });

    return flatRows;
  };

  // Helper function to get columns from a row (excluding metadata)
  const getColumnsFromRow = (row) => {
    if (!row) return [];

    const excludeKeys = [
      "path",
      "index",
      "level",
      "parentIndex",
      "variantIndex",
      "dimensionIndex",
      "inspectionIndex",
      "variantId",
      "dimensionId",
      "id",
    ];

    return Object.keys(row).filter((key) => !excludeKeys.includes(key));
  };

  // Helper function to compare two flattened rows
  const compareFlattenedRows = (oldRow, newRow) => {
    const changes = [];

    // Handle null cases
    if (!oldRow && !newRow) return changes;

    // Get all possible keys from both rows
    const oldKeys = oldRow ? Object.keys(oldRow) : [];
    const newKeys = newRow ? Object.keys(newRow) : [];
    const allKeys = new Set([...oldKeys, ...newKeys]);

    // Exclude metadata keys that shouldn't be compared
    const excludeKeys = [
      "path",
      "index",
      "level",
      "parentIndex",
      "variantIndex",
      "dimensionIndex",
      "inspectionIndex",
      "variantId",
      "dimensionId",
      "id",
    ];

    allKeys.forEach((key) => {
      if (!excludeKeys.includes(key)) {
        const oldVal = oldRow ? oldRow[key] : undefined;
        const newVal = newRow ? newRow[key] : undefined;

        if (String(oldVal) !== String(newVal)) {
          changes.push({
            field: key,
            oldValue: oldVal,
            newValue: newVal,
          });
          changedColumnsSet.add(key);
        }
      }
    });

    return changes;
  };

  // Helper function to find matching flattened row
  const findMatchingRow = (targetRow, rows) => {
    return rows?.find((row) => {
      if (targetRow.level !== row.level) return false;

      switch (targetRow.level) {
        case "parent":
          return row.id === targetRow.id;
        case "variant":
          return (
            row.parentIndex === targetRow.parentIndex &&
            row.variantId === targetRow.variantId
          );
        case "dimension":
          return (
            row.parentIndex === targetRow.parentIndex &&
            row.variantId === targetRow.variantId &&
            row.dimensionId === targetRow.dimensionId
          );
        case "inspection":
          return (
            row.parentIndex === targetRow.parentIndex &&
            row.variantId === targetRow.variantId &&
            row.dimensionId === targetRow.dimensionId &&
            row.inspectionId === targetRow.inspectionId
          );
        default:
          return false;
      }
    });
  };

  // Process each original row
  oldRows?.forEach((oldRow, oldRowIndex) => {
    const rowId = getRowId(oldRow);
    const matchingNewRow = newRows?.find(
      (newRow) => getRowId(newRow) === rowId
    );

    if (matchingNewRow) {
      // Row exists in both old and new - check for modifications
      const oldFlattenedRows = createFlattenedRows(oldRow, oldRowIndex);
      const newFlattenedRows = createFlattenedRows(matchingNewRow, oldRowIndex);

      // Compare each flattened row
      oldFlattenedRows.forEach((oldFlattened) => {
        const matchingNewFlattened = findMatchingRow(
          oldFlattened,
          newFlattenedRows
        );

        if (matchingNewFlattened) {
          const changes = compareFlattenedRows(
            oldFlattened,
            matchingNewFlattened
          );
          if (changes.length > 0) {
            changedRows.push({
              status: "modified",
              oldRow: oldFlattened,
              newRow: matchingNewFlattened,
              changes: changes,
            });
          }
        } else {
          // Add columns from deleted row
          const deletedRowColumns = getColumnsFromRow(oldFlattened);
          deletedRowColumns.forEach((col) => changedColumnsSet.add(col));

          changedRows.push({
            status: "deleted",
            oldRow: oldFlattened,
            newRow: null,
          });
        }
      });

      // Check for new flattened rows
      newFlattenedRows.forEach((newFlattened) => {
        const matchingOldFlattened = findMatchingRow(
          newFlattened,
          oldFlattenedRows
        );

        if (!matchingOldFlattened) {
          // Add columns from added row
          const addedRowColumns = getColumnsFromRow(newFlattened);
          addedRowColumns.forEach((col) => changedColumnsSet.add(col));

          // New flattened row added
          changedRows.push({
            status: "added",
            oldRow: null,
            newRow: newFlattened,
          });
        }
      });
    } else {
      // Entire row deleted
      const oldFlattenedRows = createFlattenedRows(oldRow, oldRowIndex);
      oldFlattenedRows.forEach((oldFlattened) => {
        // Add columns from deleted row
        const deletedRowColumns = getColumnsFromRow(oldFlattened);
        deletedRowColumns.forEach((col) => changedColumnsSet.add(col));

        changedRows.push({
          status: "deleted",
          oldRow: oldFlattened,
          newRow: null,
        });
      });
    }
  });

  // Check for entirely new rows
  newRows?.forEach((newRow, newRowIndex) => {
    const rowId = getRowId(newRow);
    const exists = oldRows.some((oldRow) => getRowId(oldRow) === rowId);

    if (!exists) {
      const newFlattenedRows = createFlattenedRows(newRow, newRowIndex);
      newFlattenedRows.forEach((newFlattened) => {
        // Add columns from added row
        const addedRowColumns = getColumnsFromRow(newFlattened);
        addedRowColumns.forEach((col) => changedColumnsSet.add(col));

        changedRows.push({
          status: "added",
          oldRow: null,
          newRow: newFlattened,
        });
      });
    }
  });
  const flatColumnMap = extractColumnObjects(columns);

  if (changedRows.length > 0) {
    return {
      tableName,
      tableType,
      changes: {
        columns: Array.from(changedColumnsSet)?.map((key) => {
          return flatColumnMap[key] || { value: key, name: key }; // fallback if column definition not found
        }),
        rows: changedRows,
      },
    };
  }

  return null;
};
export const detectAllChanges = (oldData, newData) => {
  const changes = [];

  // Create maps for quick lookup
  const oldItemsMap = new Map();
  const newItemsMap = new Map();

  // Helper function to flatten the hierarchical structure
  function flattenItems(items, map) {
    function traverse(item, parentPath = "") {
      const currentPath = parentPath ? `${parentPath}.${item.id}` : item.id;
      map.set(item.id, { ...item, path: currentPath });

      if (item.children && item.children.length > 0) {
        item.children.forEach((child) => traverse(child, currentPath));
      }
    }

    items.forEach((item) => traverse(item));
  }

  // Flatten both old and new data
  flattenItems(oldData, oldItemsMap);
  flattenItems(newData, newItemsMap);

  // Find added items (exist in new but not in old)
  newItemsMap.forEach((newItem, id) => {
    if (!oldItemsMap.has(id)) {
      changes.push({
        type: "added",
        id: id,
        path: newItem.path,
        data: {
          heading: newItem.heading,
          label: newItem.label,
          editorContent: newItem.editorContent,
          defaultName: newItem.defaultName,
          children: newItem.children,
        },
        timestamp: new Date().toISOString(),
      });
    }
  });

  // Find deleted items (exist in old but not in new)
  oldItemsMap.forEach((oldItem, id) => {
    if (!newItemsMap.has(id)) {
      changes.push({
        type: "deleted",
        id: id,
        path: oldItem.path,
        data: {
          heading: oldItem.heading,
          label: oldItem.label,
          editorContent: oldItem.editorContent,
          defaultName: oldItem.defaultName,
          children: oldItem.children,
        },
        timestamp: new Date().toISOString(),
      });
    }
  });

  // Find modified items (exist in both but with different content)
  oldItemsMap.forEach((oldItem, id) => {
    const newItem = newItemsMap.get(id);
    if (newItem) {
      const changedFields = getChangedFields(oldItem, newItem);
      if (changedFields.length > 0) {
        changes.push({
          type: "modified",
          id: id,
          path: oldItem.path,
          changedFields: changedFields,
          oldData: {
            heading: oldItem.heading,
            label: oldItem.label,
            editorContent: oldItem.editorContent,
            defaultName: oldItem.defaultName,
          },
          newData: {
            heading: newItem.heading,
            label: newItem.label,
            editorContent: newItem.editorContent,
            defaultName: newItem.defaultName,
          },
          timestamp: new Date().toISOString(),
        });
      }
    }
  });

  return changes;
};

export const getDynamicUsersForNotifications = async (
  id,
  selectedRole,
  userRole
) => {
  let allUserData = await getUserDataByDesignation(
    [
      {
        type: TypeConstants?.TYPE_FIND_ALL_USERS,
        ischeckCondition: true,
        masterDoc: [
          {
            documentName: FormatConstants.CAPA.PREVENTICE_ACTION,
            documentType: MasterDocumentTypeConstants.RECORD,
          },
        ],
      },
    ],
    false
  );
  // Extract user options for dropdown
  const userOptions = allUserData?.map((item) => item?.userDesignation) || [];
  if (!userRole) {
    showToast("User Role Not Available");
    return;
  }

  const preparerUser = userRole;
  let reviewerUserArr = [];
  let approvalUserArr = [];

  const userDatas = await getUserDataByDesignation(
    userRole?.departmentDefaultName !== "Top Management"
      ? [
          {
            type: TypeConstants?.TYPE_FIND_HEAD,
            department: [userRole?.departmentName],
            ischeckCondition: false,
            masterDoc: [
              {
                documentName: FormatConstants.CAPA.PREVENTICE_ACTION,
                documentType: MasterDocumentTypeConstants.RECORD,
              },
            ],
          },
          {
            type: TypeConstants?.TYPE_FIND_ALL_USERS_BY_DEPARTMENT,
            department: [userRole?.departmentDefaultName],
            ischeckCondition: false,
            masterDoc: [
              {
                documentName: FormatConstants.CAPA.PREVENTICE_ACTION,
                documentType: MasterDocumentTypeConstants.RECORD,
              },
            ],
          },
          {
            type: TypeConstants?.TYPE_FIND_ALL_USERS_BY_DESIGNATION,
            designations: [
              DesignationConstants?.MR,
              DesignationConstants?.MANAGING_DIRECTOR,
            ],
            ischeckCondition: false,
            masterDoc: [
              {
                documentName: FormatConstants.CAPA.PREVENTICE_ACTION,
                documentType: MasterDocumentTypeConstants.RECORD,
              },
            ],
          },
        ]
      : userRole?.defaultRoleName === "MR"
      ? [
          {
            type: TypeConstants?.TYPE_FIND_ALL_USERS_BY_DESIGNATION,
            designations: [
              DesignationConstants?.MR,
              DesignationConstants?.DIRECTOR,
              DesignationConstants?.MANAGING_DIRECTOR,
            ],
            ischeckCondition: false,
            masterDoc: [
              {
                documentName: FormatConstants.CAPA.PREVENTICE_ACTION,
                documentType: MasterDocumentTypeConstants.RECORD,
              },
            ],
          },
        ]
      : ["Managing Director", "Director"]?.includes(userRole?.defaultRoleName)
      ? [
          {
            type: TypeConstants?.TYPE_FIND_ALL_USERS_BY_DESIGNATION,
            designations: [
              DesignationConstants?.MANAGING_DIRECTOR,
              DesignationConstants?.DIRECTOR,
            ],
            ischeckCondition: false,
            masterDoc: [
              {
                documentName: FormatConstants.CAPA.PREVENTICE_ACTION,
                documentType: MasterDocumentTypeConstants.RECORD,
              },
            ],
          },
        ]
      : null
  );

  const addReviewerAndApproval = (reviewer, approval) => {
    reviewerUserArr?.push(...(reviewer || []));
    approvalUserArr?.push(...(approval || []));
  };

  if (userRole?.departmentDefaultName !== "Top Management") {
    const isHead =
      userDatas[TypeConstants.TYPE_FIND_HEAD]?.find(
        (item) => item?.designationId === userRole?.roleId
      ) || null;

    if (isHead) {
      const reviewer = userDatas[
        TypeConstants?.TYPE_FIND_ALL_USERS_BY_DESIGNATION
      ]?.filter((item) => item?.defaultDepartment === "MR");
      const approver = userDatas[
        TypeConstants?.TYPE_FIND_ALL_USERS_BY_DESIGNATION
      ]?.filter((item) => item?.defaultDepartment === "Managing Director");
      addReviewerAndApproval(reviewer, approver);
    } else {
      const reviewer = userDatas[TypeConstants?.TYPE_FIND_HEAD];
      const approver = userDatas[
        TypeConstants?.TYPE_FIND_ALL_USERS_BY_DESIGNATION
      ]?.filter((item) => item?.defaultDepartment === "MR");
      addReviewerAndApproval(reviewer, approver);
    }
  } else if (userRole?.defaultRoleName === "MR") {
    const reviewer = userDatas[
      TypeConstants?.TYPE_FIND_ALL_USERS_BY_DESIGNATION
    ]?.filter((item) => item?.defaultDepartment === "MR");
    const approver = userDatas[
      TypeConstants?.TYPE_FIND_ALL_USERS_BY_DESIGNATION
    ]?.filter((item) => item?.defaultDepartment === "Managing Director");
    addReviewerAndApproval(reviewer, approver);
  } else if (
    ["Managing Director", "Director"]?.includes(userRole?.defaultRoleName)
  ) {
    const reviewer = userDatas[
      TypeConstants?.TYPE_FIND_ALL_USERS_BY_DESIGNATION
    ]?.filter(
      (item) =>
        item?.defaultDepartment === "Managing Director" ||
        item?.defaultDepartment === "Director"
    );
    const approver = reviewer;
    addReviewerAndApproval(reviewer, approver);
  }

  // setReviewerUser(reviewerUserArr);
  // reviewer.current = reviewerUserArr;
  // setApproverUser(approvalUserArr);
  // approver.current = approvalUserArr;
  // setPreparer(preparerUser);
  // !id && getFormatData(userOptions);
  return {
    approvalBy: approvalUserArr?.[0],
    reviewerBy: reviewerUserArr?.[0],
    allUserData: allUserData,
    userOptions: userOptions,
    preparerBy: preparerUser,
    allApprovalUser: approvalUserArr,
    allReviewerUser: reviewerUserArr,
  };
};

export const TabPanel = (props) => {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
};

export const universalCopy = async (text) => {
  // 1) Try modern API first
  if (navigator?.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch (err) {
      console.warn("navigator.clipboard failed → fallback applied");
    }
  }

  // 2) Fallback for iPhone, Mac, Windows, Android
  try {
    const ta = document.createElement("textarea");
    ta.value = text;

    // Required for iPhone & mobile
    ta.style.position = "fixed";
    ta.style.opacity = 0;
    ta.style.left = "-9999px";
    ta.setAttribute("readonly", "");

    document.body.appendChild(ta);

    ta.select();
    ta.setSelectionRange(0, text.length); // iOS Safari requirement

    const success = document.execCommand("copy");

    document.body.removeChild(ta);
    return success;
  } catch (error) {
    console.error("Copy failed:", error);
    return false;
  }
};
const DEFAULT_FORMAT = "DD/MM/YYYY hh:mm A";

const getDateFormatFromStorage = () => {
  try {
    const constFormate = getItem(Constants?.DATE_FORMATE);

    if (constFormate && typeof constFormate === "string") {
      return constFormate;
    }

    return DEFAULT_FORMAT;
  } catch (e) {
    return DEFAULT_FORMAT;
  }
};
export const formatAutoDate = (value) => {
  if (!value) return "N/A";

  if (!isISODateString(value)) return value;

  const format = getDateFormatFromStorage();

  return moment(value).format(format);
};